import { useT } from '@platform/i18n';

interface ReviewStageProps {
  imageBase64: string;
  onRetake: () => void;
  onConfirm: () => void;
}

/**
 * Full-bleed review screen, iOS-camera style: the captured photo fills the
 * stage with Retake / Use-this-photo on a bottom overlay bar. Retakes never
 * leave this page; the success message is only posted after the user
 * confirms here (the spec forbids auto-submit of un-reviewed captures).
 *
 * Display only is mirrored to match the selfie preview the user just saw —
 * the payload sent to ESS stays in true (un-mirrored) orientation.
 */
export function ReviewStage({ imageBase64, onRetake, onConfirm }: ReviewStageProps) {
  const { t } = useT('face-capture');
  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      <img
        src={imageBase64}
        alt={t('review.imageAlt')}
        className="absolute inset-0 h-full w-full -scale-x-100 object-contain"
      />
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-6 pb-6 pt-12">
        <button
          type="button"
          onClick={onRetake}
          className="rounded-full border border-white/40 bg-black/50 px-7 py-3 text-lg font-medium text-white backdrop-blur-sm transition hover:bg-black/70"
        >
          {t('buttons.retake')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded-full bg-white px-7 py-3 text-lg font-semibold text-black transition hover:bg-zinc-200"
        >
          {t('buttons.confirm')}
        </button>
      </div>
    </div>
  );
}
